import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { todayIsoBrt } from '@/lib/dateBrt';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ImportBudgetsModal from '@/components/budgets/ImportBudgetsModal';
import ViewBudgetModal from '@/components/budgets/ViewBudgetModal';
import TemplateCrudModal from '@/components/budgets/TemplateCrudModal';
import {
  FileText,
  Plus,
  Edit3,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  Eye,
  DollarSign,
  TrendingUp,
  Clock,
  ArrowRight,
  FileSpreadsheet,
  Download,
  FileOutput,
  Layers,
  Settings,
  Copy,
  ChevronDown,
  ChevronUp,
  GripVertical,
  RotateCcw,
  BarChart3,
  Upload
} from 'lucide-react';
import logger from '@/lib/logger';

function Budgets({ user }) {
  const [activeTab, setActiveTab] = useState('proposals');
  const [budgets, setBudgets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  // Budget/Proposal modal
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [viewingBudget, setViewingBudget] = useState(null);
  const [creationMode, setCreationMode] = useState(null); // 'blank' or 'template'
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Template modal
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // CSV bulk-import modal (state owned by ImportBudgetsModal)
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Form state
  const [budgetForm, setBudgetForm] = useState({
    client_name: '',
    contact_person: '',
    client_email: '',
    company: '',
    project_name: '',
    project_type: '',
    project_description: '',
    project_location: '',
    phases: [],
    subtotal: 0,
    taxes: 0,
    total_value: 0,
    payment_method: 'by_phase',
    payment_details: '',
    observations: [],
    timeline: '',
    validity_days: 20,
    notes: '',
    template_id: null,
    // Optional admin overrides:
    proposal_number: '',
    proposal_date: '',
  });

  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    phases: []
  });

  // New observation input
  const [newObservation, setNewObservation] = useState('');

  useEffect(() => {
    fetchBudgets();
    fetchStats();
    fetchTemplates();
    fetchProjectTypes();
  }, []);

  const fetchBudgets = async () => {
    try {
      const response = await api.get('/budgets');
      setBudgets(response.data);
    } catch (error) {
      logger.error('Error fetching budgets:', error);
      toast.error('Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/budgets/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/service-templates');
      setTemplates(response.data);
    } catch (error) {
      logger.error('Error fetching templates:', error);
    }
  };

  const fetchProjectTypes = async () => {
    try {
      const response = await api.get('/project-types');
      // Sort alphabetically
      const sortedTypes = response.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setProjectTypes(sortedTypes);
    } catch (error) {
      logger.error('Error fetching project types:', error);
    }
  };

  // Calculate totals
  const calculateTotals = (phases) => {
    const subtotal = phases.reduce((sum, phase) => sum + (phase.value || 0), 0);
    const taxes = budgetForm.taxes || 0;
    return {
      subtotal,
      total_value: subtotal + taxes
    };
  };

  const updateTotals = (phases) => {
    const totals = calculateTotals(phases);
    setBudgetForm(prev => ({
      ...prev,
      phases,
      subtotal: totals.subtotal,
      total_value: totals.total_value
    }));
  };

  // Phase management
  // Stable UID generator for phase array keys (avoids index-as-key)
  const uid = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const addPhase = () => {
    const newPhase = {
      _uid: uid('phase'),
      name: `Fase ${budgetForm.phases.length + 1}`,
      order: budgetForm.phases.length,
      activities: [],
      value: 0
    };
    updateTotals([...budgetForm.phases, newPhase]);
  };

  const removePhase = (index) => {
    const newPhases = budgetForm.phases.filter((_, i) => i !== index);
    updateTotals(newPhases);
  };

  const updatePhase = (index, field, value) => {
    const newPhases = [...budgetForm.phases];
    newPhases[index] = { ...newPhases[index], [field]: value };
    
    if (field === 'value') {
      updateTotals(newPhases);
    } else {
      setBudgetForm(prev => ({ ...prev, phases: newPhases }));
    }
  };

  const addActivity = (phaseIndex) => {
    const newPhases = [...budgetForm.phases];
    newPhases[phaseIndex].activities = [...(newPhases[phaseIndex].activities || []), ''];
    setBudgetForm(prev => ({ ...prev, phases: newPhases }));
  };

  const updateActivity = (phaseIndex, actIndex, value) => {
    const newPhases = [...budgetForm.phases];
    newPhases[phaseIndex].activities[actIndex] = value;
    setBudgetForm(prev => ({ ...prev, phases: newPhases }));
  };

  const removeActivity = (phaseIndex, actIndex) => {
    const newPhases = [...budgetForm.phases];
    newPhases[phaseIndex].activities = newPhases[phaseIndex].activities.filter((_, i) => i !== actIndex);
    setBudgetForm(prev => ({ ...prev, phases: newPhases }));
  };

  // Modal handlers
  const openBlankProposal = () => {
    setCreationMode('blank');
    setSelectedTemplate(null);
    setEditingBudget(null);
    setBudgetForm({
      client_name: '',
      contact_person: '',
      client_email: '',
      company: '',
      project_name: '',
      project_type: '',
      project_description: '',
      project_location: '',
      phases: [],
      subtotal: 0,
      taxes: 0,
      total_value: 0,
      payment_method: 'by_phase',
      payment_details: '',
      observations: [],
      timeline: '',
      validity_days: 20,
      notes: '',
      template_id: null,
      proposal_number: '',
      proposal_date: '',
    });
    setBudgetModalOpen(true);
  };

  const openTemplateSelector = () => {
    setCreationMode('template');
    setSelectedTemplate(null);
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditingBudget(null);
    
    // Convert template phases to budget phases
    const phases = template.phases.map((p, idx) => ({
      name: p.name,
      order: idx,
      activities: p.activities.map(a => typeof a === 'string' ? a : a.name),
      value: p.suggested_price || 0
    }));
    
    const totals = calculateTotals(phases);
    
    setBudgetForm({
      client_name: '',
      contact_person: '',
      client_email: '',
      company: '',
      project_name: '',
      project_type: '',
      project_description: template.description || '',
      project_location: '',
      phases,
      subtotal: totals.subtotal,
      taxes: 0,
      total_value: totals.total_value,
      payment_method: 'by_phase',
      payment_details: '',
      observations: [],
      timeline: '',
      validity_days: 20,
      notes: '',
      template_id: template.template_id,
      proposal_number: '',
      proposal_date: '',
    });
    
    setCreationMode(null);
    setBudgetModalOpen(true);
  };

  const openEditBudget = (budget) => {
    setEditingBudget(budget);
    setCreationMode(null);
    setSelectedTemplate(null);
    
    // Convert items to phases if needed (legacy support)
    let phases = budget.phases || [];
    if (!phases.length && budget.items) {
      const phaseMap = {};
      budget.items.forEach(item => {
        const phaseName = item.phase_name || 'Geral';
        if (!phaseMap[phaseName]) {
          phaseMap[phaseName] = { name: phaseName, order: Object.keys(phaseMap).length, activities: [], value: 0 };
        }
        phaseMap[phaseName].activities.push(item.description);
        phaseMap[phaseName].value += item.total_value || 0;
      });
      phases = Object.values(phaseMap);
    }
    
    setBudgetForm({
      client_name: budget.client_name || '',
      contact_person: budget.contact_person || '',
      client_email: budget.client_email || '',
      company: budget.company || '',
      project_name: budget.project_name || '',
      project_type: budget.project_type || '',
      project_description: budget.project_description || '',
      project_location: budget.project_location || '',
      phases,
      subtotal: budget.subtotal || 0,
      taxes: budget.taxes || 0,
      total_value: budget.total_value || 0,
      payment_method: budget.payment_method || 'by_phase',
      payment_details: budget.payment_details || '',
      observations: budget.observations || [],
      timeline: budget.timeline || '',
      validity_days: budget.validity_days || 20,
      notes: budget.notes || '',
      template_id: budget.template_id || null,
      proposal_number: budget.proposal_number || '',
      proposal_date: budget.proposal_date || '',
    });
    
    setBudgetModalOpen(true);
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    
    if (!budgetForm.client_name || !budgetForm.project_name) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      const payload = {
        ...budgetForm,
        estimated_phases: budgetForm.phases.map(p => p.name)
      };
      
      if (editingBudget) {
        await api.put(`/budgets/${editingBudget.budget_id}`, payload);
        toast.success('Proposta atualizada com sucesso!');
      } else {
        await api.post('/budgets', payload);
        toast.success('Proposta criada com sucesso!');
      }
      
      setBudgetModalOpen(false);
      setEditingBudget(null);
      fetchBudgets();
      fetchStats();
    } catch (error) {
      logger.error('Error saving budget:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar proposta');
    }
  };

  const handleStatusChange = async (budgetId, newStatus) => {
    try {
      await api.put(`/budgets/${budgetId}/status`, { status: newStatus });
      toast.success(newStatus === 'approved' ? 'Proposta aprovada! Projeto criado automaticamente.' : 'Status atualizado!');
      fetchBudgets();
      fetchStats();
    } catch (error) {
      logger.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Deseja excluir esta proposta?')) return;
    try {
      await api.delete(`/budgets/${budgetId}`);
      toast.success('Proposta excluída!');
      fetchBudgets();
      fetchStats();
    } catch (error) {
      logger.error('Error deleting budget:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir proposta');
    }
  };

  const handleForceDelete = async (budgetId) => {
    if (!window.confirm('ATENÇÃO: Esta proposta está aprovada. Deseja excluí-la mesmo assim? O projeto criado NÃO será excluído.')) return;
    try {
      await api.delete(`/budgets/${budgetId}?force=true`);
      toast.success('Proposta excluída!');
      fetchBudgets();
      fetchStats();
    } catch (error) {
      logger.error('Error deleting budget:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir proposta');
    }
  };

  const handleRevertStatus = async (budgetId) => {
    if (!window.confirm('Deseja reverter esta proposta para rascunho?')) return;
    try {
      await api.put(`/budgets/${budgetId}/revert`);
      toast.success('Proposta revertida para rascunho!');
      fetchBudgets();
      fetchStats();
    } catch (error) {
      logger.error('Error reverting status:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reverter status');
    }
  };

  // Export handlers
  const handleExportBudgets = async () => {
    try {
      toast.info('Gerando planilha...');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/export/budgets/excel?${params.toString()}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `propostas_${todayIsoBrt()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Planilha exportada!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const handleExportProposal = async (budget, format) => {
    try {
      toast.info(`Gerando ${format.toUpperCase()}...`);
      
      // Use docx endpoint for doc format
      const endpoint = format === 'doc' ? 'docx' : format;
      const response = await api.get(`/export/proposal/${budget.budget_id}/${endpoint}`, { responseType: 'blob' });
      
      const proposalNum = (budget.proposal_number || budget.budget_id).replace('/', '_');
      const extension = format === 'doc' ? 'docx' : format === 'excel' ? 'xlsx' : 'pdf';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proposta_${proposalNum}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Documento exportado!');
    } catch (error) {
      logger.error('Export error:', error);
      toast.error('Erro ao exportar documento');
    }
  };

  // Template handlers
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      description: '',
      phases: [{ name: 'Fase I', order: 0, activities: [{ name: '' }], suggested_price: 0 }]
    });
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      phases: template.phases.map((p, idx) => ({
        name: p.name,
        order: idx,
        activities: (p.activities || []).map(a => typeof a === 'string' ? { name: a } : a),
        suggested_price: p.suggested_price || 0
      }))
    });
    setTemplateModalOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateForm.name) {
      toast.error('Nome do template é obrigatório');
      return;
    }
    
    try {
      const payload = {
        name: templateForm.name,
        description: templateForm.description,
        phases: templateForm.phases.map((p, idx) => ({
          name: p.name,
          order: idx,
          activities: p.activities.filter(a => a.name),
          suggested_price: p.suggested_price || 0
        }))
      };
      
      if (editingTemplate) {
        await api.put(`/service-templates/${editingTemplate.template_id}`, payload);
        toast.success('Template atualizado!');
      } else {
        await api.post('/service-templates', payload);
        toast.success('Template criado!');
      }
      
      setTemplateModalOpen(false);
      fetchTemplates();
    } catch (error) {
      logger.error('Error saving template:', error);
      toast.error('Erro ao salvar template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Deseja excluir este template?')) return;
    try {
      await api.delete(`/service-templates/${templateId}`);
      toast.success('Template removido!');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao excluir template');
    }
  };

  // Template phase management
  const addTemplatePhase = () => {
    setTemplateForm(prev => ({
      ...prev,
      phases: [...prev.phases, { name: `Fase ${prev.phases.length + 1}`, order: prev.phases.length, activities: [{ name: '' }], suggested_price: 0 }]
    }));
  };

  const updateTemplatePhase = (index, field, value) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[index] = { ...newPhases[index], [field]: value };
      return { ...prev, phases: newPhases };
    });
  };

  const removeTemplatePhase = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }));
  };

  const addTemplateActivity = (phaseIndex) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].activities = [...newPhases[phaseIndex].activities, { name: '' }];
      return { ...prev, phases: newPhases };
    });
  };

  const updateTemplateActivity = (phaseIndex, actIndex, value) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].activities[actIndex] = { name: value };
      return { ...prev, phases: newPhases };
    });
  };

  const removeTemplateActivity = (phaseIndex, actIndex) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].activities = newPhases[phaseIndex].activities.filter((_, i) => i !== actIndex);
      return { ...prev, phases: newPhases };
    });
  };

  // Observations management
  const addObservation = () => {
    if (!newObservation.trim()) return;
    setBudgetForm(prev => ({
      ...prev,
      observations: [...(prev.observations || []), newObservation.trim()]
    }));
    setNewObservation('');
  };

  const removeObservation = (index) => {
    setBudgetForm(prev => ({
      ...prev,
      observations: prev.observations.filter((_, i) => i !== index)
    }));
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="bg-gray-100 text-gray-700 border-gray-200">Rascunho</Badge>,
      sent: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Enviada</Badge>,
      approved: <Badge className="bg-green-100 text-green-700 border-green-200">Aprovada</Badge>,
      rejected: <Badge className="bg-red-100 text-red-700 border-red-200">Rejeitada</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const filteredBudgets = statusFilter === 'all' ? budgets : budgets.filter(b => b.status === statusFilter);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Propostas Comerciais</h1>
            <p className="text-gray-600 mt-1">Gerador de propostas e orçamentos</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleExportBudgets} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="export-budgets-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar Lista
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_budgets}</p>
                  <p className="text-xs text-gray-500">Total Propostas</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-green-200 bg-green-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                  <p className="text-xs text-green-600">Aprovadas</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-red-200 bg-red-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
                  <p className="text-xs text-red-600">Rejeitadas</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-blue-200 bg-blue-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(stats.pipeline_value)}</p>
                  <p className="text-xs text-blue-600">Pipeline</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border border-purple-200 bg-purple-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{stats.conversion_rate}%</p>
                  <p className="text-xs text-purple-600">Conversão</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="proposals">
              <FileText className="w-4 h-4 mr-2" />
              Propostas
            </TabsTrigger>
            <TabsTrigger value="templates">
              <Layers className="w-4 h-4 mr-2" />
              Modelos de Serviços
            </TabsTrigger>
          </TabsList>

          {/* Proposals Tab */}
          <TabsContent value="proposals" className="mt-4 space-y-4">
            {/* Actions and Filters */}
            <Card className="p-4 border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Filtrar:</span>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="draft">Rascunho</SelectItem>
                      <SelectItem value="sent">Enviadas</SelectItem>
                      <SelectItem value="approved">Aprovadas</SelectItem>
                      <SelectItem value="rejected">Rejeitadas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => setImportModalOpen(true)}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="btn-import-csv"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar CSV
                  </Button>
                  <Button onClick={openBlankProposal} variant="outline" className="border-emerald-300 text-emerald-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Proposta em Branco
                  </Button>
                  <Button onClick={openTemplateSelector} className="bg-emerald-800 hover:bg-emerald-900">
                    <Layers className="w-4 h-4 mr-2" />
                    Gerar de Template
                  </Button>
                </div>
              </div>
            </Card>

            {/* Proposals List */}
            <Card className="border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposta</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projeto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredBudgets.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          Nenhuma proposta encontrada
                        </td>
                      </tr>
                    ) : (
                      filteredBudgets.map((budget) => (
                        <tr key={budget.budget_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-emerald-700">{budget.proposal_number || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{budget.client_name}</p>
                              <p className="text-xs text-gray-500">{budget.company || '-'}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-900 max-w-xs truncate">{budget.project_name}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-900">{formatCurrency(budget.total_value)}</span>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(budget.status)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {budget.proposal_date
                              ? new Date(budget.proposal_date + 'T00:00:00').toLocaleDateString('pt-BR')
                              : (budget.created_at ? new Date(budget.created_at).toLocaleDateString('pt-BR') : '-')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setViewingBudget(budget); setViewModalOpen(true); }}
                                title="Visualizar"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditBudget(budget)}
                                title="Editar"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              
                              {budget.status === 'draft' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStatusChange(budget.budget_id, 'sent')}
                                  className="text-blue-600"
                                  title="Enviar"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                              )}
                              
                              {budget.status === 'sent' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusChange(budget.budget_id, 'approved')}
                                    className="text-green-600"
                                    title="Aprovar"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusChange(budget.budget_id, 'rejected')}
                                    className="text-red-600"
                                    title="Rejeitar"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              
                              {(budget.status === 'approved' || budget.status === 'rejected') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRevertStatus(budget.budget_id)}
                                  className="text-orange-600"
                                  title="Reverter para Rascunho"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              
                              <Select onValueChange={(format) => handleExportProposal(budget, format)}>
                                <SelectTrigger className="w-8 h-8 p-0 border-0">
                                  <Download className="w-4 h-4" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pdf">Exportar PDF</SelectItem>
                                  <SelectItem value="doc">Exportar DOC</SelectItem>
                                  <SelectItem value="excel">Exportar Excel</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {budget.status === 'approved' ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleForceDelete(budget.budget_id)}
                                  className="text-red-600"
                                  title="Excluir (Forçar)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(budget.budget_id)}
                                  className="text-red-600"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-4 space-y-4">
            <Card className="p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Modelos de Serviços</h3>
                  <p className="text-sm text-gray-500">Templates pré-configurados para geração rápida de propostas</p>
                </div>
                <Button onClick={openNewTemplate} className="bg-emerald-800 hover:bg-emerald-900">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Template
                </Button>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.template_id} className="p-4 border border-gray-200 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Layers className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{template.name}</h4>
                        <p className="text-xs text-gray-500">{template.phases?.length || 0} fases</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditTemplate(template)}>
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.template_id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                  )}
                  
                  <div className="space-y-1 mb-4">
                    {template.phases?.slice(0, 4).map((phase, idx) => (
                      <div key={`${template.id || template.template_id}-phase-${phase.name}-${idx}`} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 truncate">{phase.name}</span>
                        <span className="text-gray-500">{formatCurrency(phase.suggested_price)}</span>
                      </div>
                    ))}
                    {template.phases?.length > 4 && (
                      <p className="text-xs text-gray-400">+{template.phases.length - 4} fases...</p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-sm font-medium text-gray-700">
                      Total sugerido: {formatCurrency(template.phases?.reduce((sum, p) => sum + (p.suggested_price || 0), 0))}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => selectTemplate(template)} className="text-emerald-700 border-emerald-300">
                      <Copy className="w-4 h-4 mr-1" />
                      Usar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Template Selector Modal */}
      <Dialog open={creationMode === 'template'} onOpenChange={() => setCreationMode(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Selecionar Template</DialogTitle>
            <DialogDescription>Escolha um modelo de serviço para gerar a proposta automaticamente</DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {templates.map((template) => (
              <Card
                key={template.template_id}
                className="p-4 border border-gray-200 hover:border-emerald-500 cursor-pointer transition-all"
                onClick={() => selectTemplate(template)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Layers className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    <p className="text-xs text-gray-500">{template.phases?.length || 0} fases • {formatCurrency(template.phases?.reduce((s, p) => s + (p.suggested_price || 0), 0))}</p>
                  </div>
                </div>
                {template.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
                )}
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Budget/Proposal Modal */}
      <Dialog open={budgetModalOpen} onOpenChange={setBudgetModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBudget ? 'Editar Proposta' : 'Nova Proposta Comercial'}</DialogTitle>
            {selectedTemplate && (
              <DialogDescription>Baseada no template: {selectedTemplate.name}</DialogDescription>
            )}
          </DialogHeader>
          
          <form onSubmit={handleSaveBudget} className="space-y-6 mt-4">
            {/* Proposal identification (Number + Date) */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Identificação da Proposta</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="proposal_number">Número da Proposta</Label>
                  <Input
                    id="proposal_number"
                    data-testid="input-proposal-number"
                    value={budgetForm.proposal_number}
                    placeholder={editingBudget ? '' : 'Deixe em branco para auto (ex: 2026/0001)'}
                    onChange={(e) => setBudgetForm({ ...budgetForm, proposal_number: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Em branco = numeração automática. Útil informar para propostas históricas.
                  </p>
                </div>
                <div>
                  <Label htmlFor="proposal_date">Data da Proposta</Label>
                  <Input
                    id="proposal_date"
                    type="date"
                    data-testid="input-proposal-date"
                    value={budgetForm.proposal_date}
                    onChange={(e) => setBudgetForm({ ...budgetForm, proposal_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Dados do Cliente</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_name">Cliente *</Label>
                  <Input
                    id="client_name"
                    value={budgetForm.client_name}
                    onChange={(e) => setBudgetForm({ ...budgetForm, client_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="company">Empresa</Label>
                  <Input
                    id="company"
                    value={budgetForm.company}
                    onChange={(e) => setBudgetForm({ ...budgetForm, company: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="contact_person">Contato</Label>
                  <Input
                    id="contact_person"
                    value={budgetForm.contact_person}
                    onChange={(e) => setBudgetForm({ ...budgetForm, contact_person: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client_email">Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={budgetForm.client_email}
                    onChange={(e) => setBudgetForm({ ...budgetForm, client_email: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Dados do Projeto</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_name">Nome do Projeto *</Label>
                  <Input
                    id="project_name"
                    value={budgetForm.project_name}
                    onChange={(e) => setBudgetForm({ ...budgetForm, project_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="project_type">Tipo de Projeto *</Label>
                  <Select 
                    value={budgetForm.project_type} 
                    onValueChange={(v) => setBudgetForm({ ...budgetForm, project_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectTypes.map((type) => (
                        <SelectItem key={type.type_id} value={type.name}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_location">Localização</Label>
                  <Input
                    id="project_location"
                    value={budgetForm.project_location}
                    onChange={(e) => setBudgetForm({ ...budgetForm, project_location: e.target.value })}
                    placeholder="Cidade/Estado"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="project_description">Descrição do Projeto</Label>
                <Textarea
                  id="project_description"
                  value={budgetForm.project_description}
                  onChange={(e) => setBudgetForm({ ...budgetForm, project_description: e.target.value })}
                  rows={3}
                />
              </div>
            </div>

            {/* Phases/Scope */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-semibold text-gray-900">Escopo do Projeto (Fases)</h3>
                <Button type="button" onClick={addPhase} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Fase
                </Button>
              </div>
              
              <div className="space-y-4">
                {budgetForm.phases.map((phase, phaseIdx) => (
                  <Card key={phase._uid || `phase-${phaseIdx}`} className="p-4 border border-gray-200">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 grid md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <Label>Nome da Fase</Label>
                          <Input
                            value={phase.name}
                            onChange={(e) => updatePhase(phaseIdx, 'name', e.target.value)}
                            placeholder="Ex: Fase I - Diagnóstico"
                          />
                        </div>
                        <div>
                          <Label>Valor (R$)</Label>
                          <Input
                            type="number"
                            value={phase.value}
                            onChange={(e) => updatePhase(phaseIdx, 'value', parseFloat(e.target.value) || 0)}
                            step="0.01"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhase(phaseIdx)}
                        className="text-red-600 mt-6"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Atividades</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => addActivity(phaseIdx)}>
                          <Plus className="w-3 h-3 mr-1" />
                          Adicionar
                        </Button>
                      </div>
                      {phase.activities.map((activity, actIdx) => (
                        <div key={`${phase._uid || phaseIdx}-act-${actIdx}`} className="flex items-center gap-2">
                          <Input
                            value={activity}
                            onChange={(e) => updateActivity(phaseIdx, actIdx, e.target.value)}
                            placeholder="Descrição da atividade"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeActivity(phaseIdx, actIdx)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Investment Summary */}
            <Card className="p-4 border-2 border-emerald-200 bg-emerald-50">
              <h3 className="font-semibold text-emerald-800 mb-3">Investimento</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-medium">{formatCurrency(budgetForm.subtotal)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Impostos:</span>
                  <Input
                    type="number"
                    value={budgetForm.taxes}
                    onChange={(e) => {
                      const taxes = parseFloat(e.target.value) || 0;
                      setBudgetForm(prev => ({ ...prev, taxes, total_value: prev.subtotal + taxes }));
                    }}
                    className="w-32 text-right"
                    step="0.01"
                  />
                </div>
                <div className="flex justify-between pt-2 border-t border-emerald-300">
                  <span className="font-bold text-emerald-800">TOTAL:</span>
                  <span className="font-bold text-emerald-800 text-lg">{formatCurrency(budgetForm.total_value)}</span>
                </div>
              </div>
            </Card>

            {/* Payment Method */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Forma de Pagamento</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Método de Pagamento</Label>
                  <Select value={budgetForm.payment_method} onValueChange={(v) => setBudgetForm({ ...budgetForm, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="by_phase">Por etapa concluída</SelectItem>
                      <SelectItem value="monthly">Pagamentos mensais</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Validade (dias)</Label>
                  <Input
                    type="number"
                    value={budgetForm.validity_days}
                    onChange={(e) => setBudgetForm({ ...budgetForm, validity_days: parseInt(e.target.value) || 20 })}
                  />
                </div>
              </div>
              {budgetForm.payment_method === 'custom' && (
                <div>
                  <Label>Detalhes do Pagamento</Label>
                  <Textarea
                    value={budgetForm.payment_details}
                    onChange={(e) => setBudgetForm({ ...budgetForm, payment_details: e.target.value })}
                    rows={2}
                  />
                </div>
              )}
            </div>

            {/* Observations */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Observações</h3>
              <div className="flex gap-2">
                <Input
                  value={newObservation}
                  onChange={(e) => setNewObservation(e.target.value)}
                  placeholder="Adicionar observação..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addObservation())}
                />
                <Button type="button" onClick={addObservation} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {budgetForm.observations?.length > 0 && (
                <ul className="space-y-2">
                  {budgetForm.observations.map((obs, idx) => (
                    <li key={`obs-${idx}-${obs}`} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">• {obs}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeObservation(idx)} className="text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Timeline */}
            <div>
              <Label>Prazos</Label>
              <Textarea
                value={budgetForm.timeline}
                onChange={(e) => setBudgetForm({ ...budgetForm, timeline: e.target.value })}
                placeholder="Descreva os prazos de execução..."
                rows={2}
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Notas Adicionais</Label>
              <Textarea
                value={budgetForm.notes}
                onChange={(e) => setBudgetForm({ ...budgetForm, notes: e.target.value })}
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setBudgetModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                {editingBudget ? 'Salvar Alterações' : 'Criar Proposta'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Proposal Modal (extracted to component) */}
      <ViewBudgetModal
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        budget={viewingBudget}
        formatCurrency={formatCurrency}
        getStatusBadge={getStatusBadge}
        onExport={handleExportProposal}
      />

      {/* Template Modal */}
      {/* Template Modal (extracted to component) */}
      <TemplateCrudModal
        open={templateModalOpen}
        onOpenChange={setTemplateModalOpen}
        editingTemplate={editingTemplate}
        templateForm={templateForm}
        setTemplateForm={setTemplateForm}
        addPhase={addTemplatePhase}
        updatePhase={updateTemplatePhase}
        removePhase={removeTemplatePhase}
        addActivity={addTemplateActivity}
        updateActivity={updateTemplateActivity}
        removeActivity={removeTemplateActivity}
        onSubmit={handleSaveTemplate}
      />

      {/* CSV Bulk Import Modal */}
      <ImportBudgetsModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onImported={async () => {
          await fetchBudgets();
          await fetchStats();
        }}
      />
    </Layout>
  );
}

export default Budgets;
